import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { UserToken } from '../models/user';
import { CustomAuthServiceService } from '../auth/custom-auth-service.service';
import {
  DEFAULT_TIMEZONE,
  formatInZone,
  isValidTimezone,
  parseToZone,
  readBrowserTimezone,
  resolveTimezone,
  setMainTimezone,
  ResolvedTimezone,
  TimezoneSource,
} from './timezone';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  private currentUserSubject = new BehaviorSubject<UserToken | null>(null);
  readonly currentUser$ = this.currentUserSubject.asObservable();

  private clientTimezoneValue: string | null = null;
  private regionTimezoneValue: string | null = null;
  private userTimezoneValue: string | null = null;
  private facilityById: Record<number, string> = {};
  private readonly browserTimezoneValue = readBrowserTimezone();
  private useBrowser = false;

  private resolved: ResolvedTimezone = this.computeResolved();
  private timezoneSubject = new BehaviorSubject<string>(this.resolved.timezone);
  readonly timezone$: Observable<string> = this.timezoneSubject.pipe(distinctUntilChanged());

  constructor(
    private auth: CustomAuthServiceService
  ) {
    setMainTimezone(this.resolved.timezone);
    this.auth.authState$.pipe(distinctUntilChanged()).subscribe(state => {
      if (!state) {
        this.setCurrentUser(null);
        return;
      }
      this.setCurrentUser(this.auth.currentUserValue());
    });
  }

  get currentUser(): UserToken | null {
    return this.currentUserSubject.value;
  }

  setCurrentUser(user: UserToken | null): void {
    this.currentUserSubject.next(user);
    this.setUserTimezone(user?.settings?.timezone);
  }

  /**
   * Main IANA zone for UTC conversion.
   * Browser (if asked) > currentUser > region > client > America/New_York.
   * Facility is not part of this — use timezoneForFacility when a screen asks.
   */
  get timezone(): string {
    return this.resolved.timezone;
  }

  get timezoneSource(): TimezoneSource {
    return this.resolved.source;
  }

  get clientTimezone(): string | null {
    return this.clientTimezoneValue;
  }

  get regionTimezone(): string | null {
    return this.regionTimezoneValue;
  }

  get userTimezone(): string | null {
    return this.userTimezoneValue;
  }

  get browserTimezone(): string | null {
    return this.browserTimezoneValue;
  }

  setClientTimezone(zone: string | null | undefined): void {
    const next = isValidTimezone(zone) ? zone.trim() : null;
    if (this.clientTimezoneValue === next) {
      return;
    }
    this.clientTimezoneValue = next;
    this.recompute();
  }

  setRegionTimezone(zone: string | null | undefined): void {
    const next = isValidTimezone(zone) ? zone.trim() : null;
    if (this.regionTimezoneValue === next) {
      return;
    }
    this.regionTimezoneValue = next;
    this.recompute();
  }

  setUserTimezone(zone: string | null | undefined): void {
    const next = isValidTimezone(zone) ? zone.trim() : null;
    if (this.userTimezoneValue === next) {
      return;
    }
    this.userTimezoneValue = next;
    this.recompute();
  }

  /** When true, the browser zone is the main timezone. No settings screen wires this yet. */
  setUseBrowserTimezone(enabled: boolean): void {
    if (this.useBrowser === enabled) {
      return;
    }
    this.useBrowser = enabled;
    this.recompute();
  }

  /** Stored for later facility-specific conversion. Does not change the main timezone. */
  setFacilityTimezone(facilityId: number, zone: string | null | undefined): void {
    if (!isValidTimezone(zone)) {
      delete this.facilityById[facilityId];
      return;
    }
    this.facilityById[facilityId] = zone.trim();
  }

  /** Facility zone when set; otherwise the main timezone. Not used for `timezone` itself. */
  timezoneForFacility(facilityId: number): string {
    const zone = this.facilityById[facilityId];
    return isValidTimezone(zone) ? zone.trim() : this.timezone;
  }

  fromUtc(date: string | number | Date | null | undefined, zone: string = this.timezone): DateTime | null {
    return parseToZone(date, zone);
  }

  formatDate(date: string | number | Date | null | undefined, zone: string = this.timezone): string {
    return formatInZone(date, 'MM/dd/yyyy', zone);
  }

  formatTime(date: string | number | Date | null | undefined, zone: string = this.timezone): string {
    return formatInZone(date, 'h:mm a', zone);
  }

  formatDateTime(date: string | number | Date | null | undefined, zone: string = this.timezone): string {
    return formatInZone(date, 'MM/dd/yyyy hh:mm a', zone);
  }

  now(zone: string = this.timezone): DateTime {
    return DateTime.now().setZone(isValidTimezone(zone) ? zone.trim() : DEFAULT_TIMEZONE);
  }

  private computeResolved(): ResolvedTimezone {
    return resolveTimezone({
      browser: this.browserTimezoneValue,
      currentUser: this.userTimezoneValue,
      region: this.regionTimezoneValue,
      client: this.clientTimezoneValue,
    }, { useBrowser: this.useBrowser });
  }

  private recompute(): void {
    this.resolved = this.computeResolved();
    setMainTimezone(this.resolved.timezone);
    this.timezoneSubject.next(this.resolved.timezone);
  }

}
